import React, { FC, useEffect, useMemo, useRef } from "react";
import { Box, Button, Input, Modal, Typography } from "@mui/material";
import { Close } from "@mui/icons-material";
import styles from "./ReadReviewDialog.module.scss";
import IMAGES from "../../../constants/images";
import {
  DocStack,
  useAddCommentMutation,
  useFindAllDocStackLazyQuery,
  useGetCommentsLazyQuery,
} from "../../../generated/graphql";
import Moment from "../Moment/Moment";
import useAuthStore from "../../../states/zustand/authStore";
import Comment from "./Comment";
import File from "./File";
import WithPermission from "../WithPermission/WithPermission";
import { ROLES } from "../../../constants";
import useAppStore from "../../../states/zustand/appStore";
import { downloadFile } from "../../../utils/helper";
import { toast } from "react-toastify";

interface IReadReviewDialog {
  open: boolean;
  setOpen: Function;
  onClose: Function;
  docId?: string;
  title?: string;
  submissionType: string;
  submissionSubType: string;
}

const ReadReviewDialog: FC<IReadReviewDialog> = (props): JSX.Element => {
  const {
    open,
    setOpen,
    docId,
    title,
    submissionType,
    submissionSubType,
    onClose,
  } = props;
  const [getDocStack, { data, error, loading: docLoader }] =
    useFindAllDocStackLazyQuery();

  const [addComment, { loading: commentLoader }] = useAddCommentMutation();
  const [getCommentCSV, { loading: commentDownloadLoader }] =
    useGetCommentsLazyQuery();
  const { setLoader } = useAppStore();

  useEffect(() => {
    setLoader(docLoader);
  }, [docLoader]);

  useEffect(() => {
    setLoader(commentDownloadLoader);
  }, [commentDownloadLoader]);

  const commentRef = useRef("");
  const { profileData } = useAuthStore((state: any) => state);

  const handleCloseModal = () => {
    commentRef.current = "";
    onClose();
    setOpen(false);
  };

  useEffect(() => {
    if (docId && open) {
      getDocStack({
        variables: {
          docId,
        },
      });
    }
  }, [docId, open]);
  const docStacks = useMemo(() => {
    return data?.findAllDocStack || [];
  }, [data?.findAllDocStack]);

  const { latestDoc, remainingDocs } = useMemo(() => {
    const [latestDoc, ...remainingDocs] = docStacks;
    return { latestDoc, remainingDocs };
  }, [docStacks]);

  const onChangeComment = (e: React.ChangeEvent<HTMLInputElement>) => {
    commentRef.current = e.target.value;
  };

  const downloadComments = async () => {
    if (docId) {
      try {
        let res = await getCommentCSV({
          variables: {
            docId: docId,
            submissionType: submissionType,
            subType: submissionSubType,
          },
        });
        if (res.data) {
          downloadFile(res.data?.getComments);
        } else {
          throw "error";
        }
      } catch (error) {
        toast.error("Comment report cannot be downloaded.");
      }
    }
  };

  const onCommentSubmit = async () => {
    if (docId && commentRef.current && latestDoc._id) {
      await addComment({
        variables: {
          docStackId: latestDoc._id,
          comment: commentRef.current,
        },
      });
      commentRef.current = "";

      getDocStack({
        variables: {
          docId,
        },
      });
    }
  };

  return (
    <>
      <Modal
        open={open}
        onClose={handleCloseModal}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box className="modal-box">
          <Box className="modal-content">
            <Box className={`pd-x-30 pd-y-20 modal-header`}>
              <Box
                className={`d-flex align-items-center justify-content-between mg-b-10`}
              >
                <Typography
                  id="modal-modal-title"
                  variant="h2"
                  component="h2"
                  className={`mg-r-20 d-flex align-items-center`}
                >
                  <i className={`icon-chat-dot mg-r-10`}></i>
                  <span>{title}</span>
                </Typography>

                <button
                  type={`button`}
                  className={`btn btn-link text-decoration-none`}
                  onClick={handleCloseModal}
                >
                  <Close />
                </button>
              </Box>
              <Button
                variant="outlined"
                color="secondary"
                size="small"
                disabled={commentDownloadLoader}
                className={`${styles.downloadComment}`}
                onClick={downloadComments}
              >
                Download Comments
              </Button>
            </Box>

            <Box className={"modal-body"}>
              <ul className={`list-style-none ${styles.reviewList}`}>
                <li className={`${styles.reviewListItem}`}>
                  <Box className={`pd-x-30 pd-y-30`}>
                    <File
                      getDocStack={getDocStack}
                      doc={latestDoc}
                      docId={docId}
                      submissionType={submissionType}
                    />
                    <WithPermission
                      roles={[
                        ROLES.MIRB,
                        ROLES.MSMC,
                        ROLES.MIEC,
                        ROLES.ECS,
                        ROLES.QA,
                      ]}
                    >
                      <Box className={`d-flex mg-b-20`}>
                        <Box
                          className={`avatar rounded avatar-40 mg-r-8 mg-t-5`}
                        >
                          <img
                            className={`img-fluid`}
                            src={profileData?.avatar}
                            alt="profile"
                          />
                        </Box>
                        <Box className={`${styles.commentBox}`}>
                          <Input
                            type="text"
                            ref={commentRef}
                            onChange={onChangeComment}
                            className="form-control multiline"
                            name="text"
                            placeholder="Write your suggestion/recomendation here..."
                            disableUnderline={true}
                            multiline={true}
                          />
                          <Button
                            disabled={
                              docLoader ||
                              commentLoader ||
                              !latestDoc?.readAndAcknowledge
                            }
                            onClick={onCommentSubmit}
                            variant={"outlined"}
                            color={"secondary"}
                            size={"small"}
                            className={`${styles.btnSubmit}`}
                          >
                            Submit
                          </Button>
                        </Box>
                      </Box>
                    </WithPermission>

                    <ul className={`list-style-none ${styles.commentList}`}>
                      <Comment comments={latestDoc?.comments} />
                    </ul>
                  </Box>
                </li>
                {remainingDocs?.map((r) => {
                  return (
                    <li key={r._id} className={`${styles.reviewListItem}`}>
                      <Box className={`pd-x-30 pd-y-30`}>
                        <File doc={r} docId={docId} />
                        <ul className={`list-style-none ${styles.commentList}`}>
                          <Comment comments={r?.comments} />
                        </ul>
                      </Box>
                    </li>
                  );
                })}
              </ul>
            </Box>
          </Box>
        </Box>
      </Modal>
    </>
  );
};

export default ReadReviewDialog;
